import { Professional } from '@/hooks/useSearch';

export function normalizeStreet(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/^c\.\s*/i, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();
}

function isMissingField(value: unknown): boolean {
  if (value == null) return true;
  const text = String(value).trim();
  return text === '' || text.toLowerCase() === 'undefined';
}

/** "Calle 123" -> { calle: "Calle", numero: "123" }. Sin regex para evitar ReDoS. */
function splitStreetAndNumberFromDireccion(
  direccion: string
): { calle: string; numero: string } | null {
  const trimmed = direccion.trim();
  if (!trimmed) return null;

  const digitEnd = trimmed.length;
  let digitStart = digitEnd;
  while (digitStart > 0 && trimmed[digitStart - 1] >= '0' && trimmed[digitStart - 1] <= '9') {
    digitStart--;
  }
  if (digitStart === digitEnd) return null;

  let streetEnd = digitStart;
  while (streetEnd > 0 && /\s/u.test(trimmed[streetEnd - 1]!)) {
    streetEnd--;
  }
  if (streetEnd === 0 || streetEnd === digitStart) return null;

  return {
    calle: trimmed.slice(0, streetEnd),
    numero: trimmed.slice(digitStart, digitEnd),
  };
}

export function getRawCalle(professional: Professional): string {
  if (isMissingField(professional.CALLE)) return '';
  return String(professional.CALLE).trim();
}

/** Calle, número y localidad normalizados; tolera CALLE vacío o "undefined" usando DIRECCION. */
export function getNormalizedAddressParts(professional: Professional): {
  calle: string;
  numero: string;
  localidad: string;
} {
  const localidad = normalizeStreet(professional.LOCALIDAD || '');
  const rawCalle = getRawCalle(professional);
  let numero = String(professional.NUMERO ?? '').trim();
  if (numero === '0' || numero.toLowerCase() === 'undefined') numero = '';

  if (rawCalle) {
    return {
      calle: normalizeStreet(rawCalle),
      numero,
      localidad,
    };
  }

  const direccion = (professional.DIRECCION || '').trim();
  if (!direccion) {
    return { calle: '', numero: '', localidad };
  }

  const parsed = splitStreetAndNumberFromDireccion(direccion);
  if (parsed) {
    return {
      calle: normalizeStreet(parsed.calle),
      numero: parsed.numero,
      localidad,
    };
  }

  return {
    calle: normalizeStreet(direccion),
    numero: '',
    localidad,
  };
}

export function getAddressGroupKey(professional: Professional): string | null {
  const { calle, numero, localidad } = getNormalizedAddressParts(professional);

  if (!calle || !localidad) return null;

  return `${calle}|${numero}|${localidad}`;
}

export function getCoordinateGroupKey(lat: number, lng: number): string {
  return `${lat.toFixed(4)}|${lng.toFixed(4)}`;
}

/** Agrupa por dirección normalizada; si falta, usa coordenadas redondeadas. */
export function getMapLocationKey(
  professional: Professional,
  coords?: { lat: number; lng: number } | null
): string | null {
  const addressKey = getAddressGroupKey(professional);
  if (addressKey) return `addr:${addressKey}`;

  if (coords) {
    return `coord:${getCoordinateGroupKey(coords.lat, coords.lng)}`;
  }

  return null;
}
